/**
 * Floor Plan Card Selected Epic
 *
 * Reacts to floorPlanCardSelected actions and dispatches navigateTo to switch to floor plan
 * detail with the relevant selected lot / context preserved. Every floor plan card (including a
 * Similar Home one under the Sold chip, NHS20-8827) opens the same Available-chip detail
 * experience — highlighting that plan's lots, same as clicking it from its own chip's listing —
 * so when the active chip isn't already Available, this first dispatches chipSelected(Available)
 * (its existing reducer resets to a fresh Available listing) before navigateTo, which then pushes
 * that fresh state as the view-history entry Back returns to, instead of the Sold card the user
 * actually clicked from.
 *
 * A Model home without a housingUnitId yet is still rendered as a floor-plan card (it has no
 * housing unit to key a QMI-style card off of — see housingUnitCardSelectedEpic.js), but unlike a
 * generic multi-lot Available plan, it corresponds to exactly one lot. Resolving that lot here
 * (the same way housingUnitCardSelectedEpic.js resolves a QMI/Model card's lot via
 * housingUnit.lotId) keeps activeViewContext.selectedLotId — and therefore the map's
 * selected-lot highlight — in sync with the card that was actually clicked, instead of leaving
 * whatever lot happened to already be selected. Falls back to the prior carry-over behavior when
 * no Model lot matches this plan — a generic Available floor plan can span many lots, so there's
 * no single one to pin.
 *
 * @param {object} params
 * @param {object} params.dataActions - Actions from the feature data slice instance
 * @param {Observable} params.store$ - Redux store state observable
 * @returns {import('redux-observable').Epic}
 */

import { mergeMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import { ofType } from 'redux-observable';
import { VIEW_FLOOR_PLAN_DETAIL, VIEW_ENTRY_SOURCE_CARD_CLICK, VIEW_ENTRY_SOURCE_OTHER } from '../constants';
import { selectSelectedLotId, selectLots, selectActiveChip } from './selectors';
import LotStatus from '../enums/lotStatus';

function createFloorPlanCardSelectedEpic({ dataActions, store$ }) {
  return (action$) => action$.pipe(
    ofType(dataActions.floorPlanCardSelected.type),
    withLatestFrom(store$),
      mergeMap(([action, state]) => {
          const activeChip = selectActiveChip(state);
          const planId = Number(action.payload);
          const modelLot = selectLots(state).find((lot) => (
              lot.status === LotStatus.Model.toString() && lot.housePlanIds?.includes(planId)));
          // A Similar Home click only ever happens from the Sold chip's own listing (Sold cards
          // themselves aren't clickable — see components/soldHomeCard.dom.js) and always targets
          // an unrelated plan, so the previously selected sold lot must not carry over into the
          // new detail view (NHS20-8827 selectedLotId leak).
          const isSimilarHomeClick = activeChip === LotStatus.Sold.toString();
          const selectedLotId = modelLot?.sitePlanLotId ??
              (isSimilarHomeClick ? null : (selectSelectedLotId(state) ?? null));
          const navigateToAction = dataActions.navigateTo({
              view: VIEW_FLOOR_PLAN_DETAIL,
              context: {
                  planId: Number.isFinite(planId) ? planId : null,
                  selectedLotId,
                  housingUnitId: null,
                  source: isSimilarHomeClick ? VIEW_ENTRY_SOURCE_OTHER : VIEW_ENTRY_SOURCE_CARD_CLICK,
              },
          });

      return activeChip === LotStatus.Available.toString() || activeChip === LotStatus.Model.toString()
        ? of(navigateToAction)
        : of(dataActions.chipSelected(LotStatus.Available.toString()), navigateToAction);
    }),
  );
}

export default createFloorPlanCardSelectedEpic;
